import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  AlertTriangle, AlertCircle, Info, Calendar, FileText,
  ChevronDown, ChevronUp, ExternalLink, Check, Bell, Loader2
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";


const SEVERITY_CONFIG = {
  critical: {
    icon: AlertTriangle,
    label: "Critical",
    labelCs: "Kritická",
    bgColor: "bg-red-50 dark:bg-red-900/20",
    borderColor: "border-red-200 dark:border-red-800",
    badgeColor: "bg-red-500",
    textColor: "text-red-700 dark:text-red-300",
  },
  important: {
    icon: AlertCircle,
    label: "Important",
    labelCs: "Důležitá",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
    borderColor: "border-amber-200 dark:border-amber-800",
    badgeColor: "bg-amber-500",
    textColor: "text-amber-700 dark:text-amber-300",
  },
  informational: {
    icon: Info,
    label: "Informational",
    labelCs: "Informační",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    borderColor: "border-blue-200 dark:border-blue-800",
    badgeColor: "bg-blue-500",
    textColor: "text-blue-700 dark:text-blue-300",
  },
};

const TYPE_LABELS = {
  regulatory_change: { en: "Regulatory Change", cs: "Regulatorní změna" },
  template_update: { en: "Template Update", cs: "Aktualizace šablony" },
  system_info: { en: "System Info", cs: "Systémová informace" },
};

export default function NewsRegulations() {
  const { getAuthHeaders } = useAuth();
  const { t } = useLanguage();
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [markingRead, setMarkingRead] = useState(null);

  const fetchBulletins = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("type", typeFilter);
      if (severityFilter !== "all") params.append("severity", severityFilter);
      
      const res = await api.get(`/bulletins?${params}`, {
        
      });
      setBulletins(res.data.bulletins || []);
    } catch (err) {
      toast.error(t.news?.errorLoading || t.errors?.failedToLoadNews || "Failed to load bulletins");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, severityFilter, t]);

  useEffect(() => {
    fetchBulletins();
  }, [fetchBulletins]);

  const handleMarkRead = async (bulletinId, severity) => {
    try {
      setMarkingRead(bulletinId);
      const confirmed = severity === "critical";
      await api.post(`/bulletins/${bulletinId}/read?confirmed=${confirmed}`);
      
      // Update local state
      setBulletins((prev) =>
        prev.map((b) =>
          b.id === bulletinId ? { ...b, is_read: true, read_at: new Date().toISOString() } : b
        )
      );
      
      // Dispatch event to notify the notification badge to refresh
      window.dispatchEvent(new CustomEvent('bulletinRead'));
      
      toast.success(t.common?.markedAsRead || "Marked as read");
    } catch (err) {
      toast.error(t.errors?.failedToUpdate || "Failed to mark as read");
    } finally {
      setMarkingRead(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const getSeverityBadge = (severity) => {
    const config = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.informational;
    const Icon = config.icon;
    return (
      <Badge className={`${config.badgeColor} text-white`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="news-regulations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">
            {t.news?.title || "News & Regulations"}
          </h1>
          <p className="text-[var(--text-muted)]">
            {t.news?.subtitle || "Stay informed about regulatory changes and important updates"}
          </p>
        </div>
        <Bell className="w-6 h-6 text-[var(--text-muted)]" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList>
            <TabsTrigger value="all">{t.news?.tabs?.all || "All"}</TabsTrigger>
            <TabsTrigger value="regulatory_change">{t.news?.tabs?.regulatory || "Regulatory"}</TabsTrigger>
            <TabsTrigger value="template_update">{t.news?.tabs?.templates || "Templates"}</TabsTrigger>
            <TabsTrigger value="system_info">{t.news?.tabs?.system || "System"}</TabsTrigger>
          </TabsList>
        </Tabs>

        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-[180px]" data-testid="severity-filter">
            <SelectValue placeholder={t.news?.severities?.allSeverities || "All severities"} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t.news?.severities?.allSeverities || "All Severities"}</SelectItem>
            <SelectItem value="critical">{t.news?.severities?.critical || "Critical"}</SelectItem>
            <SelectItem value="important">{t.news?.severities?.important || "Important"}</SelectItem>
            <SelectItem value="informational">{t.news?.severities?.informational || "Informational"}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulletins List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[var(--text-muted)]" />
        </div>
      ) : bulletins.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-50" />
          <h3 className="text-lg font-medium text-[var(--text-primary)]">No bulletins found</h3>
          <p className="text-[var(--text-muted)]">
            Check back later for regulatory updates and news
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {bulletins.map((bulletin) => {
            const config = SEVERITY_CONFIG[bulletin.severity] || SEVERITY_CONFIG.informational;
            const isExpanded = expandedId === bulletin.id;
            const Icon = config.icon;

            return (
              <div
                key={bulletin.id}
                className={`border rounded-lg overflow-hidden transition-all ${config.borderColor} ${
                  !bulletin.is_read ? config.bgColor : "bg-[var(--card-bg)]"
                }`}
                data-testid={`bulletin-card-${bulletin.id}`}
              >
                {/* Header */}
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => toggleExpand(bulletin.id)}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${config.bgColor}`}>
                        <Icon className={`w-5 h-5 ${config.textColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {getSeverityBadge(bulletin.severity)}
                          <Badge variant="secondary">
                            {TYPE_LABELS[bulletin.type]?.en || bulletin.type}
                          </Badge>
                          {!bulletin.is_read && (
                            <Badge variant="outline" className="bg-white dark:bg-gray-800">
                              New
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-[var(--text-primary)] truncate">
                          {bulletin.title}
                        </h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-muted)]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Effective: {new Date(bulletin.effective_date).toLocaleDateString()}
                          </span>
                          {bulletin.is_read && bulletin.read_at && (
                            <span className="flex items-center gap-1 text-green-600">
                              <Check className="w-4 h-4" />
                              Read
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!bulletin.is_read && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(bulletin.id, bulletin.severity);
                          }}
                          disabled={markingRead === bulletin.id}
                          data-testid={`mark-read-${bulletin.id}`}
                        >
                          {markingRead === bulletin.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Mark Read
                            </>
                          )}
                        </Button>
                      )}
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-[var(--text-muted)]" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-[var(--text-muted)]" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-[var(--card-border)]">
                    <div className="pt-4 prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown>{bulletin.content}</ReactMarkdown>
                    </div>

                    {bulletin.related_kb_doc_filename && (
                      <div className="mt-4 flex items-center gap-2 p-3 bg-[var(--bg-secondary)] rounded-lg">
                        <FileText className="w-4 h-4 text-[var(--text-muted)]" />
                        <span className="text-sm text-[var(--text-muted)]">
                          Related document:
                        </span>
                        <span className="text-sm font-medium text-[var(--text-primary)]">
                          {bulletin.related_kb_doc_filename}
                        </span>
                        <ExternalLink className="w-4 h-4 text-[var(--text-muted)]" />
                      </div>
                    )}

                    {!bulletin.is_read && bulletin.severity === "critical" && (
                      <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                          This is a critical bulletin. Please review the content and confirm you have read it.
                        </p>
                        <Button
                          className="mt-2 bg-red-600 hover:bg-red-700"
                          onClick={() => handleMarkRead(bulletin.id, bulletin.severity)}
                          disabled={markingRead === bulletin.id}
                          data-testid={`confirm-read-${bulletin.id}`}
                        >
                          {markingRead === bulletin.id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Check className="w-4 h-4 mr-2" />
                          )}
                          I have read and understood
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
